"use client";

import { CustomButton, FlexCenter, FlexCol } from "@/components";
import InputDataContent from "@/components/inquiryDetails/inputDataContent";
import { IBaseLabelValue } from "@/interfaces";
import {
  IFreewayTollBill,
  IRawPurchasedFreewayTollInquiryResData,
} from "@/interfaces/inquiries/vehicle";
import { formatNumberWithDots } from "@/lib";
import { routes } from "@/routes";
import { useRouter } from "next/navigation";
import { useMemo } from "react";

const formatPrice = (value?: number | string | null) => {
  if (value === undefined || value === null || value === "") return "0 ریال";

  return `${formatNumberWithDots(value)} ریال`;
};

const FreewayTollInquiryDetails = ({
  data,
}: {
  data: IRawPurchasedFreewayTollInquiryResData;
}) => {
  const router = useRouter();
  const storedPayload = (data as any)?.data ?? {};
  const inquiryData = storedPayload?.data ?? storedPayload;
  const inquiryInput =
    storedPayload?.input ?? (data as any).user_input ?? inquiryData?.input ?? {};
  const result = inquiryData?.result;
  const bills = useMemo<IFreewayTollBill[]>(
    () =>
      Array.isArray(result?.bills)
        ? result.bills.filter(Boolean)
        : [],
    [result?.bills],
  );
  const plateParts = [
    inquiryInput.plk_serial,
    inquiryInput.plk2,
    inquiryInput.plk3,
    inquiryInput.plk1,
  ].filter(Boolean);
  const plateText = plateParts.length ? plateParts.join(" ") : "-";

  const inputContent: IBaseLabelValue[] = [
    {
      label: "شماره پلاک",
      value: plateText,
    },
  ];

  const resultContent: IBaseLabelValue[] = [
    {
      label: "مبلغ کل عوارض",
      value: formatPrice(result?.totalAmount ?? 0),
    },
    {
      label: "تعداد عوارض",
      value: String(bills.length),
    },
    {
      label: "شناسه پیگیری",
      value: inquiryData?.trackId || "-",
    },
  ];

  return (
    <div className="shadow-main from-sky-bliss relative basis-[500px] rounded-2xl bg-linear-to-b to-transparent p-[2px] lg:basis-[760px]">
      <FlexCol className="items-stretch rounded-2xl bg-white p-8">
        <h2 className="mb-5 text-center font-bold lg:text-lg">
          نتیجه استعلام عوارض آزادراهی
        </h2>

        <InputDataContent
          content={inputContent}
          className="mb-6 p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />

        <div className="mb-6 rounded-2xl border border-slate-200 bg-slate-50 p-5">
          <p className="text-sm text-slate-500">مبلغ قابل پرداخت</p>
          <p className="mt-2 text-2xl font-bold text-slate-900">
            {formatPrice(result?.totalAmount)}
          </p>
        </div>

        <InputDataContent
          content={resultContent}
          className="mb-6 p-6 md:p-8"
          itemClassName="flex-col md:flex-row leading-9 md:leading-none"
        />

        {bills.length ? (
          <div className="mb-8 overflow-hidden rounded-2xl border border-slate-200">
            <div className="hidden grid-cols-5 bg-slate-100 px-4 py-3 text-sm font-bold text-slate-700 md:grid">
              <span>شناسه پرداخت</span>
              <span>تاریخ</span>
              <span>مبلغ</span>
              <span>کد عوارضی</span>
              <span>کد آزادراه</span>
            </div>
            <div className="divide-y divide-slate-100">
              {bills.map((bill, index) => (
                <div
                  key={`${bill.paymentId || "freeway-toll"}-${index}`}
                  className="grid gap-3 px-4 py-4 text-sm text-slate-700 md:grid-cols-5"
                >
                  <div>
                    <span className="mb-1 block text-xs text-slate-400 md:hidden">
                      شناسه پرداخت
                    </span>
                    {bill?.paymentId || "-"}
                  </div>
                  <div>
                    <span className="mb-1 block text-xs text-slate-400 md:hidden">
                      تاریخ
                    </span>
                    {bill?.date || "-"}
                  </div>
                  <div>
                    <span className="mb-1 block text-xs text-slate-400 md:hidden">
                      مبلغ
                    </span>
                    {formatPrice(bill?.price ?? 0)}
                  </div>
                  <div>
                    <span className="mb-1 block text-xs text-slate-400 md:hidden">
                      کد عوارضی
                    </span>
                    {bill?.gateway || "-"}
                  </div>
                  <div>
                    <span className="mb-1 block text-xs text-slate-400 md:hidden">
                      کد آزادراه
                    </span>
                    {bill?.freeway || "-"}
                  </div>
                </div>
              ))}
            </div>
          </div>
        ) : (
          <div className="mb-8 rounded-2xl border border-emerald-200 bg-emerald-50 p-5 text-center text-sm text-emerald-700">
            برای این پلاک عوارض آزادراهی ثبت‌شده‌ای یافت نشد.
          </div>
        )}

        <FlexCenter className="flex flex-col justify-center md:flex-row md:gap-6">
          <CustomButton
            id="details-freeway-toll-back"
            className="w-fit bg-gray-500"
            onClick={() => router.push(routes.main.INQUIRY_HISTORY.primaryPath)}
          >
            بازگشت
          </CustomButton>
          <CustomButton
            id="details-freeway-toll-new-request"
            onClick={() => router.push(routes.services.Freeway_Toll.rewritePath)}
            className="bg-primary-main mx-auto w-fit px-8! text-white"
          >
            استعلام جدید
          </CustomButton>
        </FlexCenter>
      </FlexCol>
    </div>
  );
};

export default FreewayTollInquiryDetails;
